import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Plus, Search, Video, AlertCircle } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { canViewHandbookTraining, canManageHandbookTraining } from '../utils/handbookTrainingAccess';
import {
  buildHandbookTrainingTree,
  findHandbookTrainingTreeNode,
  folderDepthLabel,
} from '../utils/handbookTrainingTree';
import {
  deleteHandbookTrainingNode,
  insertHandbookTrainingNodeAmongSiblings,
  moveHandbookTrainingNode,
  reorderHandbookTrainingSiblings,
  subscribeHandbookTrainingFreshnessReports,
  subscribeHandbookTrainingNodes,
  updateFreshnessReportStatus,
} from '../utils/handbookTrainingFirestore';
import { isHandbookTrainingVideoNew } from '../utils/handbookTrainingAccess';
import {
  markHandbookTrainingVideoWatched,
  subscribeHandbookTrainingWatchedNodeIds,
} from '../utils/handbookTrainingViews';
import type { HandbookTrainingNode } from '../types/handbookTraining';
import HandbookHubTabs from '../components/people/handbook/HandbookHubTabs';
import HandbookTrainingTree from '../components/people/handbook/HandbookTrainingTree';
import HandbookTrainingLoomPlayer from '../components/people/handbook/HandbookTrainingLoomPlayer';
import HandbookTrainingFolderCards from '../components/people/handbook/HandbookTrainingFolderCards';
import HandbookTrainingEditor from '../components/people/handbook/HandbookTrainingEditor';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './PeopleHubPage.css';

type EditorState =
  | { mode: 'closed' }
  | { mode: 'create'; kind: 'folder' | 'video'; parentId: string | null; parentDepth: number }
  | { mode: 'edit'; nodeId: string };

const PeopleHandbookTrainingPage: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const { nodeId: routeNodeId } = useParams<{ nodeId?: string }>();
  const navigate = useNavigate();

  const [nodes, setNodes] = useState<HandbookTrainingNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [editor, setEditor] = useState<EditorState>({ mode: 'closed' });
  const [freshnessOpen, setFreshnessOpen] = useState(false);
  const [freshnessReports, setFreshnessReports] = useState<
    import('../types/handbookTraining').HandbookTrainingFreshnessReport[]
  >([]);
  const [watchedNodeIds, setWatchedNodeIds] = useState<Set<string>>(() => new Set());

  const me = useMemo(() => crmUsers.find(u => u.uid === user?.id), [crmUsers, user?.id]);
  const designations = me?.designations ?? user?.designations;
  const authRole = me?.role ?? user?.role;
  const canManage = useMemo(
    () => canManageHandbookTraining({ authRole, designations }),
    [authRole, designations],
  );

  useEffect(() => {
    setLoading(true);
    const unsub = subscribeHandbookTrainingNodes(
      list => {
        setNodes(list);
        setLoading(false);
      },
      err => {
        console.error('[handbookTraining] nodes subscription', err);
        setLoading(false);
      },
      { forEditors: canManage },
    );
    return unsub;
  }, [canManage]);

  useEffect(() => {
    if (!canManage) return;
    return subscribeHandbookTrainingFreshnessReports(setFreshnessReports);
  }, [canManage]);

  useEffect(() => {
    if (!user?.id) return;
    return subscribeHandbookTrainingWatchedNodeIds(user.id, setWatchedNodeIds, err =>
      console.error('[handbookTraining] views subscription', err),
    );
  }, [user?.id]);

  const publishedOnly = !canManage;
  const tree = useMemo(
    () => buildHandbookTrainingTree(nodes, { publishedOnly }),
    [nodes, publishedOnly],
  );

  const selectedId = routeNodeId || null;

  const selectedFolderTreeNode = useMemo(() => {
    if (!selectedId) return undefined;
    return findHandbookTrainingTreeNode(tree, selectedId);
  }, [tree, selectedId]);

  const folderDirectVideos = useMemo(() => {
    if (!selectedFolderTreeNode || selectedFolderTreeNode.kind !== 'folder') return [];
    return selectedFolderTreeNode.children.filter(c => c.kind === 'video');
  }, [selectedFolderTreeNode]);

  const folderSubfolders = useMemo(() => {
    if (!selectedFolderTreeNode || selectedFolderTreeNode.kind !== 'folder') return [];
    return selectedFolderTreeNode.children.filter(c => c.kind === 'folder');
  }, [selectedFolderTreeNode]);
  const selectedNode = useMemo(
    () => (selectedId ? nodes.find(n => n.id === selectedId) : undefined),
    [nodes, selectedId],
  );

  useEffect(() => {
    if (!user?.id || editor.mode !== 'closed') return;
    if (selectedNode?.kind !== 'video' || !selectedNode.published) return;
    if (watchedNodeIds.has(selectedNode.id)) return;
    setWatchedNodeIds(prev => {
      if (prev.has(selectedNode.id)) return prev;
      const next = new Set(prev);
      next.add(selectedNode.id);
      return next;
    });
    void markHandbookTrainingVideoWatched(user.id, selectedNode.id).catch(err =>
      console.error('[handbookTraining] mark watched', err),
    );
  }, [user?.id, selectedNode, editor.mode, watchedNodeIds]);

  const openReports = useMemo(
    () => freshnessReports.filter(r => r.status === 'open'),
    [freshnessReports],
  );

  const selectNode = useCallback(
    (id: string) => {
      setEditor({ mode: 'closed' });
      navigate(`/people/handbook/training/${id}`);
    },
    [navigate],
  );

  const editorNode = editor.mode === 'edit' ? nodes.find(n => n.id === editor.nodeId) : null;

  const handleMoveNode = useCallback(
    async (nodeId: string, newParentId: string | null) => {
      if (!user?.id || !canManage) return;
      try {
        await moveHandbookTrainingNode(nodeId, newParentId, nodes, user.id);
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Move failed');
      }
    },
    [canManage, nodes, user?.id],
  );

  const handleInsertSibling = useCallback(
    async (draggedId: string, targetId: string, insertBefore: boolean) => {
      if (!user?.id || !canManage) return;
      try {
        await insertHandbookTrainingNodeAmongSiblings(
          draggedId,
          targetId,
          insertBefore,
          nodes,
          user.id,
        );
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Reorder failed');
      }
    },
    [canManage, nodes, user?.id],
  );

  if (!user || !canViewHandbookTraining(user.role)) {
    return <Navigate to="/" replace />;
  }

  const onTreeSelect = (id: string, kind: 'folder' | 'video') => {
    selectNode(id);
    if (kind === 'folder' && canManage) {
      /* folder selected — show folder summary in main pane */
    }
  };

  const findDepth = (node: HandbookTrainingNode): number => {
    let d = 0;
    let pid = node.parentId;
    while (pid) {
      d++;
      pid = nodes.find(n => n.id === pid)?.parentId ?? null;
    }
    return d;
  };

  const handleDelete = async () => {
    if (!selectedNode || !canManage) return;
    if (!window.confirm(`Delete "${selectedNode.title}"?`)) return;
    try {
      await deleteHandbookTrainingNode(selectedNode.id, nodes);
      navigate('/people/handbook/training');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const moveSibling = async (dir: -1 | 1) => {
    if (!selectedNode || !canManage) return;
    const parentId = selectedNode.parentId;
    const siblings = nodes
      .filter(n => (n.parentId || null) === (parentId || null))
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const idx = siblings.findIndex(s => s.id === selectedNode.id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= siblings.length) return;
    const ids = siblings.map(s => s.id);
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    await reorderHandbookTrainingSiblings(parentId, ids);
  };

  const moveSelectedToTopLevel = async () => {
    if (!selectedNode || !canManage || !user?.id) return;
    if (selectedNode.parentId == null) return;
    await handleMoveNode(selectedNode.id, null);
  };

  return (
    <div className="people-hub-route-root people-hub-handbook-route-root">
      <div className="ops-hub-toolbar people-handbook-index-toolbar">
        <div className="ops-hub-toolbar-left">
          <div>
            <h2 className="ops-hub-title people-handbook-index-hub-title">
              <Video size={20} aria-hidden className="people-handbook-hub-title-icon" />
              Company handbook
            </h2>
            <HandbookHubTabs />
          </div>
        </div>
        <div className="ops-hub-toolbar-right people-handbook-toolbar-right">
          {canManage && openReports.length > 0 && (
            <button
              type="button"
              className="ops-stat ops-stat-action people-handbook-training-freshness-btn"
              onClick={() => setFreshnessOpen(v => !v)}
            >
              <AlertCircle size={14} aria-hidden />
              {openReports.length} freshness report{openReports.length === 1 ? '' : 's'}
            </button>
          )}
          {canManage && (
            <>
              <button
                type="button"
                className="ops-new-btn"
                onClick={() =>
                  setEditor({
                    mode: 'create',
                    kind: 'folder',
                    parentId: selectedNode?.kind === 'folder' ? selectedNode.id : selectedNode?.parentId ?? null,
                    parentDepth: selectedNode?.kind === 'folder' ? findDepth(selectedNode) + 1 : selectedNode ? findDepth(selectedNode) : 0,
                  })
                }
              >
                <Plus size={14} aria-hidden />
                Add folder
              </button>
              <button
                type="button"
                className="ops-new-btn"
                onClick={() =>
                  setEditor({
                    mode: 'create',
                    kind: 'video',
                    parentId: selectedNode?.kind === 'folder' ? selectedNode.id : selectedNode?.parentId ?? null,
                    parentDepth: selectedNode ? (selectedNode.kind === 'folder' ? findDepth(selectedNode) : findDepth(selectedNode)) : 0,
                  })
                }
              >
                <Plus size={14} aria-hidden />
                Add video
              </button>
            </>
          )}
          <div className="people-handbook-search-field">
            <label htmlFor="people-training-search" className="people-handbook-search-label">
              <Search size={14} aria-hidden className="people-handbook-search-icon" />
              <span className="sr-only">Search training</span>
            </label>
            <input
              id="people-training-search"
              type="search"
              className="people-handbook-search-input"
              placeholder="Search training…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
        </div>
      </div>

      {freshnessOpen && canManage && (
        <div className="people-handbook-training-freshness-panel people-hub-panel">
          <h3 className="people-hub-panel-title">Agent freshness reports</h3>
          <ul className="people-hub-list">
            {freshnessReports.slice(0, 20).map(r => (
              <li key={r.id} className="people-hub-list-item">
                <div>
                  <strong>{r.videoSlug}</strong>
                  <span className={`people-hub-status people-hub-status-${r.status}`}> {r.status}</span>
                  <p className="people-hub-muted">{r.findings.slice(0, 280)}</p>
                </div>
                {r.status === 'open' && (
                  <button
                    type="button"
                    className="pm-btn pm-btn-ghost pm-btn-sm"
                    onClick={() => void updateFreshnessReportStatus(r.id, 'acknowledged')}
                  >
                    Acknowledge
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="ops-hub-loading">
          <div className="ops-hub-loading-bar" />
        </div>
      ) : (
        <div className="people-handbook-training-layout">
          <aside className="people-handbook-training-sidebar people-hub-panel">
            <HandbookTrainingTree
              tree={tree}
              flatNodes={nodes}
              watchedNodeIds={watchedNodeIds}
              selectedId={selectedId}
              searchQuery={searchInput}
              canManage={canManage}
              onSelect={onTreeSelect}
              onMoveNode={(nodeId, newParentId) => void handleMoveNode(nodeId, newParentId)}
              onInsertSibling={(draggedId, targetId, insertBefore) =>
                void handleInsertSibling(draggedId, targetId, insertBefore)
              }
            />
          </aside>
          <main className="people-handbook-training-main people-hub-panel">
            {editor.mode !== 'closed' ? (
              <HandbookTrainingEditor
                mode={editor.mode}
                kind={
                  editor.mode === 'create'
                    ? editor.kind
                    : editorNode?.kind === 'video'
                      ? 'video'
                      : 'folder'
                }
                parentId={editor.mode === 'create' ? editor.parentId : editorNode?.parentId ?? null}
                parentDepth={
                  editor.mode === 'create'
                    ? editor.parentDepth
                    : editorNode
                      ? findDepth(editorNode)
                      : 0
                }
                node={editor.mode === 'edit' ? editorNode ?? null : null}
                allNodes={nodes}
                userId={user.id}
                onSaved={id => {
                  setEditor({ mode: 'closed' });
                  selectNode(id);
                }}
                onCancel={() => setEditor({ mode: 'closed' })}
              />
            ) : selectedNode?.kind === 'video' ? (
              <>
                <h3 className="people-hub-panel-title people-handbook-training-video-title">
                  {selectedNode.title}
                  {isHandbookTrainingVideoNew(selectedNode, watchedNodeIds) && (
                    <span className="people-handbook-training-tree-new">New</span>
                  )}
                </h3>
                {selectedNode.description && (
                  <p className="people-hub-muted">{selectedNode.description}</p>
                )}
                {selectedNode.loomUrl && (
                  <div className="people-handbook-training-video-frame">
                    <HandbookTrainingLoomPlayer loomUrl={selectedNode.loomUrl} title={selectedNode.title} />
                  </div>
                )}
                {selectedNode.outline && selectedNode.outline.length > 0 && (
                  <div className="people-handbook-training-chapters">
                    <h4>In this video</h4>
                    <ul>
                      {selectedNode.outline.map(ch => (
                        <li key={ch.id}>
                          {ch.title} <span className="people-hub-muted">({ch.t}s)</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {canManage && (
                  <div className="people-handbook-training-actions">
                    <button
                      type="button"
                      className="pm-btn pm-btn-ghost"
                      onClick={() => setEditor({ mode: 'edit', nodeId: selectedNode.id })}
                    >
                      Edit
                    </button>
                    {selectedNode.parentId != null && (
                      <button
                        type="button"
                        className="pm-btn pm-btn-ghost"
                        onClick={() => void moveSelectedToTopLevel()}
                      >
                        Move to top level
                      </button>
                    )}
                    <button type="button" className="pm-btn pm-btn-ghost" onClick={() => void moveSibling(-1)}>
                      Move up
                    </button>
                    <button type="button" className="pm-btn pm-btn-ghost" onClick={() => void moveSibling(1)}>
                      Move down
                    </button>
                    <button type="button" className="pm-btn pm-btn-ghost" onClick={() => void handleDelete()}>
                      Delete
                    </button>
                  </div>
                )}
              </>
            ) : selectedNode?.kind === 'folder' ? (
              <>
                <h3 className="people-hub-panel-title">{selectedNode.title}</h3>
                {selectedNode.description && (
                  <p className="people-hub-muted">{selectedNode.description}</p>
                )}
                <HandbookTrainingFolderCards
                  videos={folderDirectVideos}
                  subfolders={folderSubfolders}
                  watchedNodeIds={watchedNodeIds}
                  canManage={canManage}
                  onSelectVideo={id => selectNode(id)}
                  onSelectFolder={id => selectNode(id)}
                  onReorderVideo={(draggedId, targetId, insertBefore) =>
                    void handleInsertSibling(draggedId, targetId, insertBefore)
                  }
                />
                {canManage && (
                  <div className="people-handbook-training-actions">
                    <button
                      type="button"
                      className="pm-btn pm-btn-ghost"
                      onClick={() => setEditor({ mode: 'edit', nodeId: selectedNode.id })}
                    >
                      Edit folder
                    </button>
                    {selectedNode.parentId != null && (
                      <button
                        type="button"
                        className="pm-btn pm-btn-ghost"
                        onClick={() => void moveSelectedToTopLevel()}
                      >
                        Move to top level
                      </button>
                    )}
                    <button type="button" className="pm-btn pm-btn-ghost" onClick={() => void moveSibling(-1)}>
                      Move up
                    </button>
                    <button type="button" className="pm-btn pm-btn-ghost" onClick={() => void moveSibling(1)}>
                      Move down
                    </button>
                    <button type="button" className="pm-btn pm-btn-ghost" onClick={() => void handleDelete()}>
                      Delete
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="people-hub-muted">Select a training video from the library.</p>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default PeopleHandbookTrainingPage;
